import {
  Building2,
  ChevronDown,
  Check,
  Plus,
  Loader2,
} from 'lucide-react'


import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUnreadCountsByWorkspace } from '@/stores/notification.store'
import { cn } from '@/utils/cn'

import { useWorkspaceSwitcher } from '../hooks/useWorkspaceSwitcher'

function UnreadBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-none text-white',
        className
      )}
      aria-label={`${count} unread notification${count === 1 ? '' : 's'}`}
    >
      {count > 9 ? '9+' : count}
    </span>
  )
}

interface WorkspaceSwitcherProps {
  isCollapsed?: boolean
  onWorkspaceSelect?: () => void
  onOpenCreateModal?: () => void
}

export function WorkspaceSwitcher({
  isCollapsed,
  onWorkspaceSelect,
  onOpenCreateModal,
}: WorkspaceSwitcherProps) {
  const {
    currentWorkspace,
    workspaces,
    switchWorkspace,
    isLoading,
  } = useWorkspaceSwitcher()

  const unreadByWorkspace = useUnreadCountsByWorkspace()
  // The bell already shows the current workspace; the trigger flags activity elsewhere.
  const unreadElsewhere = workspaces.reduce(
    (sum, ws) => (ws.id === currentWorkspace.id ? sum : sum + (unreadByWorkspace[ws.id] ?? 0)),
    0
  )

  const handleSelect = async (workspaceId: string) => {
    const switched = await switchWorkspace(workspaceId)

    if (switched) {
      onWorkspaceSelect?.()
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'h-auto w-full justify-between gap-2 p-1.5 hover:bg-neutral-100/80 focus-visible:ring-0 focus-visible:ring-offset-0',
            isCollapsed
              ? 'justify-center px-1'
              : 'px-2'
          )}
        >
          <div className="flex min-w-0 items-center gap-3 overflow-hidden">
            <span className="relative shrink-0">
              <Building2
                size={21}
                strokeWidth={2}
                className="text-neutral-700"
              />
              {isCollapsed && unreadElsewhere > 0 && (
                <span
                  className="absolute right-0 top-0 h-2 w-2 rounded-full bg-red-600 ring-2 ring-white"
                  aria-label={`${unreadElsewhere} unread in other workspaces`}
                />
              )}
            </span>

            {!isCollapsed && (
              <span className="truncate text-sm font-semibold text-neutral-900">
                {currentWorkspace.name}
              </span>
            )}
          </div>

          {!isCollapsed && (
            <div className="flex shrink-0 items-center gap-1.5">
              <UnreadBadge
                count={unreadElsewhere}
                className="h-4 min-w-4 text-[10px]"
              />
              <ChevronDown
                size={17}
                strokeWidth={2}
                className="shrink-0 text-neutral-500"
              />
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-64 p-1"
      >
        {isLoading && workspaces.length === 0 && (
          <div className="flex items-center justify-center gap-2 p-3 text-xs text-neutral-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading workspaces...
          </div>
        )}

        {workspaces.map((ws) => (
          <DropdownMenuItem
            key={ws.id}
            onClick={() => handleSelect(ws.id)}
            className="flex cursor-pointer items-center justify-between py-2.5 text-sm"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <Building2
                size={18}
                strokeWidth={2}
                className="shrink-0 text-neutral-500"
              />

              <span className="truncate font-medium">
                {ws.name}
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <UnreadBadge count={unreadByWorkspace[ws.id] ?? 0} />
              {ws.id === currentWorkspace.id && (
                <Check
                  size={16}
                  strokeWidth={2}
                  className="shrink-0 text-indigo-600"
                />
              )}
            </div>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer gap-2 py-2.5 text-sm font-medium text-indigo-600"
          onClick={onOpenCreateModal}
        >
          <Plus
            size={16}
            strokeWidth={2}
          />
          Create Workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}